import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  CreateTicketInput,
  DataEnvelope,
  ListEnvelope,
  Message,
  ReplyInput,
  Ticket,
  TicketChannel,
  TicketCounts,
  TicketPriority,
  UpdateTicketInput,
} from './types';

export interface TicketFilters {
  q?: string;
  /** Comma-separated statuses */
  status?: string;
  priority?: TicketPriority | '';
  channel?: TicketChannel | '';
  assignee?: string;
  teamId?: string;
  customerId?: string;
  tag?: string;
}

export const ticketsKey = ['tickets'] as const;

// No polling: lists, counts and open tickets refresh when the realtime socket says they changed
// (see app/realtime).
export function useTicketsInfinite(filters: TicketFilters) {
  return useInfiniteQuery({
    queryKey: [...ticketsKey, 'list', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api<ListEnvelope<Ticket>>('/tickets', { query: { ...filters, limit: 25, cursor: pageParam }, signal }),
    getNextPageParam: (last) => last.pagination.nextCursor ?? undefined,
  });
}

export function useTicketCounts() {
  return useQuery({
    queryKey: [...ticketsKey, 'counts'],
    queryFn: ({ signal }) =>
      api<DataEnvelope<TicketCounts>>('/tickets/counts', { signal }).then((r) => r.data),
  });
}

export function useTicket(id: string | undefined) {
  return useQuery({
    queryKey: [...ticketsKey, 'detail', id],
    enabled: !!id,
    queryFn: ({ signal }) => api<DataEnvelope<Ticket>>(`/tickets/${id}`, { signal }).then((r) => r.data),
  });
}

export function useTicketMessages(id: string | undefined) {
  return useQuery({
    queryKey: [...ticketsKey, 'detail', id, 'messages'],
    enabled: !!id,
    queryFn: ({ signal }) =>
      api<ListEnvelope<Message>>(`/tickets/${id}/messages`, { query: { limit: 200 }, signal }).then(
        (r) => r.data,
      ),
  });
}

function useInvalidateTickets() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ticketsKey });
    void qc.invalidateQueries({ queryKey: ['customers'] });
  };
}

export function useCreateTicket() {
  const invalidate = useInvalidateTickets();
  return useMutation({
    mutationFn: (input: CreateTicketInput) =>
      api<DataEnvelope<Ticket>>('/tickets', { method: 'POST', body: input }).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useUpdateTicket() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTickets();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTicketInput }) =>
      api<DataEnvelope<Ticket>>(`/tickets/${id}`, { method: 'PATCH', body: input }).then((r) => r.data),
    onSuccess: (ticket) => {
      qc.setQueryData([...ticketsKey, 'detail', ticket.id], ticket);
      invalidate();
    },
  });
}

export function useReply(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReplyInput) =>
      api<DataEnvelope<Message>>(`/tickets/${ticketId}/messages`, { method: 'POST', body: input }).then(
        (r) => r.data,
      ),
    // Show the reply at once; lists and counts refresh from the socket's `tickets:changed`.
    onSuccess: (message) => {
      const detailKey = [...ticketsKey, 'detail', ticketId];
      qc.setQueryData<Message[]>([...detailKey, 'messages'], (prev) =>
        prev && !prev.some((m) => m.id === message.id) ? [...prev, message] : prev,
      );
      // A reply can change status/assignee.
      void qc.invalidateQueries({ queryKey: detailKey, exact: true });
    },
  });
}
